/**
 * Trader Score Card
 *
 * A radar/spider chart showing multiple trading performance metrics
 * with a composite "Trader Score" calculation.
 */

import React, { useMemo, useEffect } from "react";
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  ResponsiveContainer,
} from "recharts";
import { cn } from "@/lib/utils";
import { InfoIcon } from "lucide-react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import type { AdvancedStats, BehaviorAnalysis } from "@nordex/core";

/** Styled tooltip content classes */
const tooltipContentClass = cn(
  "z-50 max-w-[280px] rounded-md px-3 py-2",
  "bg-card/95 text-foreground/90 backdrop-blur-sm",
  "border border-border dark:border-muted-foreground/20 shadow-lg",
  "animate-in fade-in-0 zoom-in-95",
  "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
  "data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2"
);

// Inject keyframes for the score card glow
const injectScoreStyles = () => {
  const styleId = "trader-score-glow-animation";
  if (document.getElementById(styleId)) return;

  const style = document.createElement("style");
  style.id = styleId;
  style.textContent = `
    @keyframes score-pulse {
      0%, 100% { opacity: 0.4; }
      50% { opacity: 0.7; }
    }
    @keyframes score-glow {
      0%, 100% { filter: drop-shadow(0 0 4px var(--glow-color)); }
      50% { filter: drop-shadow(0 0 8px var(--glow-color)); }
    }
  `;
  document.head.appendChild(style);
};

interface TraderScoreCardProps {
  stats: AdvancedStats;
  behavior: BehaviorAnalysis;
  className?: string;
}

interface MetricData {
  metric: string;
  value: number;
  fullMark: 100;
  rawValue: string;
  description: string;
}

/**
 * Normalize a value to 0-100 scale with bounds
 */
function normalize(
  value: number,
  min: number,
  max: number,
  invert = false
): number {
  const clamped = Math.max(min, Math.min(max, value));
  const normalized = ((clamped - min) / (max - min)) * 100;
  return invert ? 100 - normalized : normalized;
}

/**
 * Calculate individual metric scores
 */
function calculateMetricScores(
  stats: AdvancedStats,
  behavior: BehaviorAnalysis
): MetricData[] {
  // Win Rate: 0-100% maps to 0-100
  const winRateScore = normalize(stats.winRate, 0, 100);

  // Profit Factor: 0-3+ maps to 0-100 (1.0 = 33, 2.0 = 66, 3.0 = 100)
  const pfValue = stats.profitFactor === Infinity ? 3 : stats.profitFactor;
  const profitFactorScore = normalize(pfValue, 0, 3);

  // Avg Win/Loss Ratio: 0-5 maps to 0-100
  const rrValue =
    stats.riskRewardRatio === Infinity ? 5 : stats.riskRewardRatio;
  const avgWinLossScore = normalize(rrValue, 0, 5);

  // Recovery Factor: -1 to 3 maps to 0-100
  const recoveryScore = normalize(stats.recoveryFactor, -1, 3);

  // Max Drawdown: 100% = bad (0), 0% = good (100) - inverted
  const drawdownScore = normalize(stats.maxDrawdownPercent, 0, 100, true);

  // Consistency: size consistency score is already 0-100, but higher CV = less consistent
  // So we invert it: 100 - sizeConsistencyScore
  const consistencyScore = Math.max(0, 100 - behavior.sizeConsistencyScore);

  return [
    {
      metric: "Win %",
      value: winRateScore,
      fullMark: 100,
      rawValue: `${stats.winRate.toFixed(1)}%`,
      description: "Percentage of winning trades",
    },
    {
      metric: "Profit Factor",
      value: profitFactorScore,
      fullMark: 100,
      rawValue:
        stats.profitFactor === Infinity
          ? "∞"
          : stats.profitFactor.toFixed(2),
      description: "Gross profit / Gross loss (>1 = profitable)",
    },
    {
      metric: "Avg Win/Loss",
      value: avgWinLossScore,
      fullMark: 100,
      rawValue:
        stats.riskRewardRatio === Infinity
          ? "∞"
          : `${stats.riskRewardRatio.toFixed(2)}:1`,
      description: "Average winning trade / Average losing trade",
    },
    {
      metric: "Recovery",
      value: recoveryScore,
      fullMark: 100,
      rawValue: stats.recoveryFactor.toFixed(2),
      description: "Net profit / Max drawdown",
    },
    {
      metric: "Max DD",
      value: drawdownScore,
      fullMark: 100,
      rawValue: `${stats.maxDrawdownPercent.toFixed(1)}%`,
      description: "Maximum drawdown from peak (lower is better)",
    },
    {
      metric: "Consistency",
      value: consistencyScore,
      fullMark: 100,
      rawValue: `${consistencyScore.toFixed(0)}%`,
      description: "Position sizing consistency (higher is better)",
    },
  ];
}

/**
 * Calculate composite trader score (weighted average)
 */
function calculateCompositeScore(metrics: MetricData[]): number {
  // Weights for each metric (should sum to 1)
  const weights: Record<string, number> = {
    "Win %": 0.2,
    "Profit Factor": 0.25,
    "Avg Win/Loss": 0.15,
    Recovery: 0.15,
    "Max DD": 0.15,
    Consistency: 0.1,
  };

  let weightedSum = 0;
  let totalWeight = 0;

  for (const metric of metrics) {
    const weight = weights[metric.metric] || 0;
    weightedSum += metric.value * weight;
    totalWeight += weight;
  }

  return totalWeight > 0 ? weightedSum / totalWeight : 0;
}

/**
 * Get score color based on value
 */
function getScoreColor(score: number): string {
  if (score >= 70) return "#2ebd85"; // Green
  if (score >= 50) return "#f0b90b"; // Yellow/Gold
  if (score >= 30) return "#f6a623"; // Orange
  return "#f6465d"; // Red
}

/**
 * Get score label based on value
 */
function getScoreLabel(score: number): string {
  if (score >= 80) return "Excellent";
  if (score >= 70) return "Very Good";
  if (score >= 60) return "Good";
  if (score >= 50) return "Average";
  if (score >= 40) return "Below Avg";
  if (score >= 30) return "Needs Work";
  return "Poor";
}

/** Placeholder metric data for empty state - just for axis labels */
const placeholderMetricData: MetricData[] = [
  { metric: "Win %", value: 0, fullMark: 100, rawValue: "—", description: "Percentage of winning trades" },
  { metric: "Profit Factor", value: 0, fullMark: 100, rawValue: "—", description: "Gross profit / Gross loss (>1 = profitable)" },
  { metric: "Avg Win/Loss", value: 0, fullMark: 100, rawValue: "—", description: "Average winning trade / Average losing trade" },
  { metric: "Recovery", value: 0, fullMark: 100, rawValue: "—", description: "Net profit / Max drawdown" },
  { metric: "Max DD", value: 0, fullMark: 100, rawValue: "—", description: "Maximum drawdown from peak (lower is better)" },
  { metric: "Consistency", value: 0, fullMark: 100, rawValue: "—", description: "Position sizing consistency (higher is better)" },
];

export function TraderScoreCard({
  stats,
  behavior,
  className,
}: TraderScoreCardProps) {
  // Inject styles on mount
  useEffect(() => {
    injectScoreStyles();
  }, []);

  const metricData = useMemo(
    () => calculateMetricScores(stats, behavior),
    [stats, behavior]
  );

  const compositeScore = useMemo(
    () => calculateCompositeScore(metricData),
    [metricData]
  );

  const scoreColor = getScoreColor(compositeScore);
  const scoreLabel = getScoreLabel(compositeScore);

  // Check if we have enough data
  const hasData = stats.totalTrades >= 5;

  // Use placeholder data for axis labels when no data
  const chartData = hasData ? metricData : placeholderMetricData;

  return (
    <div
      className={cn(
        "rounded-lg border border-border/50 bg-card shadow-sm h-full",
        className
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-border/30">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-foreground">Trader Score</h3>
          <TooltipPrimitive.Provider delayDuration={250}>
            <TooltipPrimitive.Root>
              <TooltipPrimitive.Trigger asChild>
                <button type="button" className="text-muted-foreground/50 hover:text-muted-foreground transition-colors">
                  <InfoIcon className="size-3.5" />
                </button>
              </TooltipPrimitive.Trigger>
              <TooltipPrimitive.Portal>
                <TooltipPrimitive.Content side="top" sideOffset={5} className={tooltipContentClass}>
                  <p className="text-xs leading-relaxed">
                    A composite score based on win rate, profit factor, risk/reward,
                    recovery factor, drawdown, and consistency. Higher is better.
                  </p>
                </TooltipPrimitive.Content>
              </TooltipPrimitive.Portal>
            </TooltipPrimitive.Root>
          </TooltipPrimitive.Provider>
        </div>
      </div>

      {/* Content - flex column to push score to bottom */}
      <div className="p-3 flex flex-col h-[calc(100%-44px)]">
        {/* Radar Chart with enhanced grid - takes up available space */}
        <div className="flex-1 min-h-[240px] w-full relative">
          {/* Custom Grid Dots Pattern - SVG overlay */}
          <svg 
            className="absolute inset-0 w-full h-full pointer-events-none"
            viewBox="0 0 200 200"
            preserveAspectRatio="xMidYMid meet"
          >
            <GridDotsPattern cx={100} cy={100} outerRadius={80} />
          </svg>
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart cx="50%" cy="50%" outerRadius="80%" data={chartData}>
              <PolarGrid
                stroke="hsl(var(--border))"
                strokeOpacity={0.3}
                gridType="polygon"
              />
              <PolarAngleAxis
                dataKey="metric"
                tick={({ payload, x, y, textAnchor }) => {
                  const metric = chartData.find(m => m.metric === payload.value);
                  const numX = Number(x) || 0;
                  const numY = Number(y) || 0;
                  const adjustedY = payload.value === "Win %" ? numY - 10 : 
                                   payload.value === "Recovery" ? numY + 14 : numY;
                  const adjustedX = textAnchor === "start" ? numX + 10 : 
                                   textAnchor === "end" ? numX - 10 : numX;
                  return (
                    <TooltipPrimitive.Provider delayDuration={250}>
                      <TooltipPrimitive.Root>
                        <TooltipPrimitive.Trigger asChild>
                          <g>
                            <text
                              x={adjustedX}
                              y={adjustedY}
                              textAnchor={textAnchor}
                              className="fill-foreground text-[10px] font-medium"
                              style={{ textDecoration: 'underline', textDecorationStyle: 'dashed', textUnderlineOffset: '2px', cursor: 'default', userSelect: 'none' }}
                            >
                              {payload.value}
                            </text>
                          </g>
                        </TooltipPrimitive.Trigger>
                        <TooltipPrimitive.Portal>
                          <TooltipPrimitive.Content side="top" sideOffset={5} className={tooltipContentClass}>
                            <p className="text-xs leading-relaxed">{metric?.description}</p>
                          </TooltipPrimitive.Content>
                        </TooltipPrimitive.Portal>
                      </TooltipPrimitive.Root>
                    </TooltipPrimitive.Provider>
                  );
                }}
                tickLine={false}
              />
              {/* Only render the Radar shape when we have data */}
              {hasData && (
                <Radar
                  name="Score"
                  dataKey="value"
                  stroke={scoreColor}
                  fill={scoreColor}
                  fillOpacity={0.25}
                  strokeWidth={2}
                  dot={{
                    r: 3,
                    fill: scoreColor,
                    strokeWidth: 0,
                  }}
                  style={{
                    ["--glow-color" as string]: `${scoreColor}80`,
                    animation: "score-glow 2s ease-in-out infinite",
                  }}
                />
              )}
            </RadarChart>
          </ResponsiveContainer>
          
          {/* Empty state overlay message */}
          {!hasData && (
            <div className="absolute inset-0 flex items-center justify-center">
              <p className="text-sm text-muted-foreground text-center px-4">
                Complete at least 5 trades to see your Trader Score
              </p>
            </div>
          )}
        </div>

        {/* Score Display - anchored to bottom */}
        <div className="flex items-center justify-between pt-3 mt-auto border-t border-border/30">
          <div className="text-sm text-muted-foreground">Your Trader Score</div>
          <div className="flex items-center gap-3">
            <span
              className="text-xl font-bold"
              style={{ color: hasData ? scoreColor : "hsl(var(--muted-foreground))" }}
            >
              {hasData ? compositeScore.toFixed(2) : "—"}
            </span>
            <ScoreGauge score={hasData ? compositeScore : 0} color={hasData ? scoreColor : "hsl(var(--muted-foreground))"} />
            {hasData && (
              <span
                className="text-xs font-medium px-2 py-0.5 rounded"
                style={{
                  backgroundColor: `${scoreColor}20`,
                  color: scoreColor,
                }}
              >
                {scoreLabel}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Grid dots pattern for the radar chart background
 * Creates hexagonal lines, radial lines, and dots at every intersection
 */
function GridDotsPattern({ 
  cx, 
  cy, 
  outerRadius 
}: { 
  cx: number; 
  cy: number; 
  outerRadius: number;
}) {
  const angles = 6; // 6 metrics = hexagon
  const levels = [0.2, 0.4, 0.6, 0.8, 1.0]; // 5 concentric rings
  
  const elements: React.ReactNode[] = [];
  
  // 1. Draw hexagonal lines at each level (connecting dots at same radius)
  for (const level of levels) {
    const points: string[] = [];
    for (let i = 0; i < angles; i++) {
      const angle = (i * 360 / angles - 90) * (Math.PI / 180);
      const radius = outerRadius * level;
      const x = cx + radius * Math.cos(angle);
      const y = cy + radius * Math.sin(angle);
      points.push(`${x},${y}`);
    }
    elements.push(
      <polygon
        key={`hex-${level}`}
        points={points.join(' ')}
        fill="none"
        className="stroke-muted-foreground/20"
        strokeWidth={0.5}
      />
    );
  }
  
  // 2. Draw radial lines from center to each vertex
  for (let i = 0; i < angles; i++) {
    const angle = (i * 360 / angles - 90) * (Math.PI / 180);
    const x = cx + outerRadius * Math.cos(angle);
    const y = cy + outerRadius * Math.sin(angle);
    elements.push(
      <line
        key={`radial-${i}`}
        x1={cx}
        y1={cy}
        x2={x}
        y2={y}
        className="stroke-muted-foreground/15"
        strokeWidth={0.5}
      />
    );
  }
  
  // 3. Draw dots at each grid intersection
  for (const level of levels) {
    for (let i = 0; i < angles; i++) {
      const angle = (i * 360 / angles - 90) * (Math.PI / 180);
      const radius = outerRadius * level;
      const x = cx + radius * Math.cos(angle);
      const y = cy + radius * Math.sin(angle);
      
      elements.push(
        <circle
          key={`dot-${level}-${i}`}
          cx={x}
          cy={y}
          r={2}
          className="fill-muted-foreground/50"
        />
      );
    }
  }
  
  // 4. Add center dot
  elements.push(
    <circle
      key="center-dot"
      cx={cx}
      cy={cy}
      r={2}
      className="fill-muted-foreground/50"
    />
  );
  
  return <g>{elements}</g>;
}

/**
 * Mini score gauge visualization
 */
function ScoreGauge({ score, color }: { score: number; color: string }) {
  // Create gradient stops for the gauge
  const gradientId = `score-gauge-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <div className="relative w-24 h-3 rounded-full overflow-hidden bg-muted/50">
      {/* Gradient background */}
      <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#f6465d" />
            <stop offset="30%" stopColor="#f6a623" />
            <stop offset="50%" stopColor="#f0b90b" />
            <stop offset="70%" stopColor="#2ebd85" />
            <stop offset="100%" stopColor="#2ebd85" />
          </linearGradient>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill={`url(#${gradientId})`}
          opacity="0.3"
        />
      </svg>

      {/* Score indicator */}
      <div
        className="absolute top-0 h-full w-1 bg-foreground rounded-full transition-all duration-500"
        style={{
          left: `${Math.min(score, 100)}%`,
          transform: "translateX(-50%)",
          boxShadow: `0 0 6px ${color}`,
        }}
      />

      {/* Value markers */}
      <div className="absolute inset-0 flex justify-between px-0.5 items-center">
        {[0, 20, 40, 60, 80, 100].map((mark) => (
          <div
            key={mark}
            className="w-px h-1.5 bg-muted-foreground/30"
          />
        ))}
      </div>
    </div>
  );
}

